import { RoseTree } from '@tableau/data-structures';
import { DestinationItem, DestinationItemType, NestedItemBasicInfo, NestedItemsFractalRowStore, NestedItemsTreeHelper, NestedProjectExplorer } from '@tableau/nested-project-explorer';
import * as React from 'react';

import { getAppStore } from '~src/app/AppStore';
import { SessionUserSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { Formatters, Messages } from '~src/codegen/Localize';
import { createMultipleItemResultNotificationValues, IErrorDetail, MultipleItemResultNotificationValues } from '~src/commonInfra/contentActions/ActionMessages';
import { ContentActionNotification } from '~src/commonInfra/contentActions/ContentActionNotification';
import { IErrorItem, MultipleItemResult } from '~src/commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { openReactAlertDialog } from '~src/commonInfra/dialogs/ReactAlertDialog';
import { openReactConfirmationDialog, ReactConfirmationDialogBodyProps } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ProjectErrors } from '~src/content/services/ProjectErrors';
import { IDataSourceBase } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { DataSourceErrors } from '~src/datasource/services/DataSourceErrors';
import { hasDraftIdPrefix } from '~src/drafts/utils/DraftUtils';
import { Flows } from '~src/flow/resources/Flows';
import { FlowErrors } from '~src/flow/services/FlowErrors';
import { IMultipleContentTypeResult,IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { IMetricInfo } from '~src/metrics/models/IMetricInfo';
import { MetricContentType } from '~src/metrics/models/MetricContentType';
import { Metrics } from '~src/metrics/resources/Metrics';
import { MetricErrors } from '~src/metrics/services/MetricErrors';
import { selectPersonalSpace } from '~src/personalSpace/selectors/PersonalSpaceSelector';
import { DestinationProjectContentType, DestinationProjects } from '~src/projects/resources/DestinationProjects';
import { Projects } from '~src/projects/resources/Projects';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { ListResult } from '~src/ts/fetchers/ListResult';
import { SliceableHelper } from '~src/ts/helpers/SliceableHelper';
import { L10n } from '~src/ts/L10n/L10n';
import { DestinationProjectMapper } from '~src/ts/mappers/DestinationProjectMapper';
import {
  DataRoleContentType,
  DatasourceContentType,
  FlowContentType,
  IContentType,
  LensContentType,
  ProjectContentType,
  PublishedConnectionContentType,
  WorkbookContentType
} from '~src/ts/models/ContentTypes';
import { FlowBaseInfo } from '~src/ts/models/FlowInfo';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { IDataRoleBase } from '~src/ts/models/IDataRoleInfo';
import { ILensBase } from '~src/ts/models/ILensInfo';
import { IProjectBase } from '~src/ts/models/IProjectInfo';
import { IPublishedConnectionInfo } from '~src/ts/models/IPublishedConnectionInfo';
import { IIdItemInfo, INamedItemInfo } from '~src/ts/models/resourceInfoParts';
import { Set } from '~src/ts/models/Set';
import { DataRoles } from '~src/ts/resources/Dataroles';
import { Lenses } from '~src/ts/resources/Lenses';
import { ContentItem } from '~src/ts/ServerApi/requests/MoveContentToContainerRequest';
import { ContentDraftStatus } from '~src/ts/ServerApi/types/ContentDraft';
import { IDestinationProject } from '~src/ts/ServerApi/types/IDestinationProject';
import { IProjectName } from '~src/ts/ServerApi/types/IProject';
import { DataRoleErrors } from '~src/ts/services/DataRoleErrors';
import { LensErrors } from '~src/ts/services/LensErrors';
import { PublishedConnectionErrors } from '~src/ts/services/PublishedConnectionErrors';
import { IWorkbookBase } from '~src/workbook/models/IWorkbookInfo';
import { Workbooks } from '~src/workbook/resources/Workbooks';
import { WorkbookErrors } from '~src/workbook/services/WorkbookErrors';

import { Location } from '../../common/react/Location';
import { MoveContentDialogBody, MoveContentDialogBodyProps, MoveContentDialogBodyState } from '../components/MoveContentDialogBody';
import { IResultWithDestinationProject } from './MoveToProjectAction';
import _ = require('underscore');
import { isUndefined } from 'underscore';

import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { getExplorableItemDefinition } from '~src/contentDefinitionInterfaces/ContentDefinitionsLists';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { CommonErrorCode, ContentErrorCode } from '~src/ts/helpers/ErrorCodes';
import { ExplorableItemType } from '~src/ts/models/ContentType';

export namespace MoveContentAction {

  const NoPermission = 'NO_PERMISSION';

  export function confirmMoveContent(content: IContentInfo[]): Promise<IResultWithDestinationProject> {

    const projects = Set.fromIdItemArray(content.filter(i => i.contentType === ProjectContentType.name));
    const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === WorkbookContentType.name));
    const datasources = Set.fromIdItemArray(content.filter(i => i.contentType === DatasourceContentType.name));
    const flows = Set.fromIdItemArray(content.filter(i => i.contentType === FlowContentType.name));
    const dataRoles = Set.fromIdItemArray(content.filter(i => i.contentType === DataRoleContentType.name));
    const lenses = Set.fromIdItemArray(content.filter(i => i.contentType === LensContentType.name));
    const metrics = Set.fromIdItemArray(content.filter(i => i.contentType === MetricContentType.name));
    const publishedConnections = Set.fromIdItemArray(content.filter(i => i.contentType === PublishedConnectionContentType.name));
    const count = content.length;
    let selectedItem: NestedItemBasicInfo = null;
    const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    const personalSpaceStatus = _.some(content, (item) => item.owner && item.owner.id === userId);

    return initNestedProjectExplorer(projects, workbooks, datasources, flows, dataRoles, lenses, metrics, publishedConnections, personalSpaceStatus).then((result) => {
      if (result === NoPermission) {
        return alertCantMoveContent(Messages.permissionsLongMessage_cannotMoveItems({ ITEM_COUNT_LOC: Formatters.formatCount(content.length) })).result;
      }

      const actionDisabled = (bodyProps: MoveContentDialogBodyProps, dialogState: MoveContentDialogBodyState) => {
        return dialogState.selectedItem === null;
      };

      const dialogInstance = openReactActionDialog<MoveContentDialogBodyProps, MoveContentDialogBodyState, any>({
        title: Messages.moveContent(),
        initialBodyProps: {
          prompt: Messages.selectProjectForContent({ ITEM_COUNT: count, ITEM_NAME: L10n.elideIfLong(content[0].name), ITEM_COUNT_LOC: Formatters.formatCount(count) }),
          targets: result as NestedProjectExplorer.Props
        },
        initialDialogState: {
          selectedItem: selectedItem
        },
        bodyComponent: MoveContentDialogBody,
        actionName: Messages.confirmMoveContent({ ITEM_COUNT: count, ITEM_COUNT_LOC: Formatters.formatCount(count) }),
        action: (props: MoveContentDialogBodyProps, state: MoveContentDialogBodyState) => {
          selectedItem = state.selectedItem;
          if (FeatureFlags.personalSpaceEnabled && selectedItem.itemType === DestinationItemType.personalSpace) {
            return null;
          }
          return moveContent(projects, workbooks, datasources, flows, dataRoles, lenses, metrics, publishedConnections, content, state.selectedItem);
        },
        actionDisabled,
        isActionDelete: false,
        dismissDialogOnActionFailed: true,
        dialogWidth: TabDialogWidth.width375px
      }) as any;

      return dialogInstance.result.then((result: any) => {
        dialogInstance.dismiss();

        if (FeatureFlags.personalSpaceEnabled && selectedItem.itemType === DestinationItemType.personalSpace) {
          const contentItems = getContentItemsList(content);
          const contentName = getContentName(workbooks, datasources, contentItems.length);
          const modalInstance = openReactConfirmationDialog({
            title: Messages.moveToPersonalSpaceWarningTitle(),
            message: Messages.moveToPersonalSpaceWarningPrimaryMessage({ITEM_COUNT: contentItems.length, CONTENT_TITLE: contentName}),
            actionName: Messages.moveToPersonalSpaceWarningAction(),
            bodyComponent: getPersonalSpaceWarningDialog,
            action: () => {
              return moveContent(projects, workbooks, datasources, flows, dataRoles, lenses, metrics, publishedConnections, content, selectedItem);
            },
            dialogWidth: TabDialogWidth.width500px,
            paramsForAction: null,
          });
          return Promise.resolve(modalInstance.result);
        }
        return { ...ResultHelper.combineResults(result), destinationProjectId: getDestinationProjectIdFromDialog(selectedItem) };
      });
    });
  }

  export function getPersonalSpaceWarningDialog(props: ReactConfirmationDialogBodyProps): JSX.Element {
    return(<div>
              <div>
                {props.message}
              </div>
              <div style ={{marginTop: '20px'}}>
                {Messages.moveToPersonalSpaceWarningSecondaryMessage() + ' '}
                <a href={HelpService.getHelpUrlByCategory('server-personal-move')} target="_blank" rel="noopener noreferrer">{Messages.moveToPersonalSpaceWarningLearnMore()}</a>
              </div>
          </div>);
  }

  function getContentName(workbooks: Set<IContentInfo>, datasources: Set<IContentInfo>, numberOfItems: number) {
    if (numberOfItems === 1 && workbooks.size() === 1) {
      return workbooks.getByIndex(0).name;
    } else if (numberOfItems === 1 && datasources.size() === 1) {
      return datasources.getByIndex(0).name;
    }

    return '';
  }

  function alertCantMoveContent(message: string) {
    return openReactAlertDialog({
      title: Messages.permissions_cannotMoveItems(),
      message
    });
  }

  function getDestinationProjectIdFromDialog(selectedProject: NestedItemBasicInfo): string {
    const destinationProjectId = selectedProject.id;
    return (destinationProjectId === NestedItemsFractalRowStore.SentinelRootProjectId ? null : destinationProjectId);
    // API expects a null ID if we're moving a project to the top level of the site
  }

  function initNestedProjectExplorer(
    projects: Set<IContentInfo>,
    workbooks: Set<IContentInfo>,
    datasources: Set<IContentInfo>,
    flows: Set<IContentInfo>,
    dataRoles: Set<IContentInfo>,
    lenses: Set<IContentInfo>,
    metrics: Set<IContentInfo>,
    publishedConnections: Set<IContentInfo>,
    personalSpaceStatus: boolean
  ): Promise<{}>{

    // The Nested Project Explorer presents the user a tree-view showing all the projects on the site,
    // along with their "status" (whether or not the items can actually be moved there).
    // We call the getDestinationProjects API to fetch the information to build this tree, however,
    // due to the limitations of the API, we split the source items by content type as well
    // as in groups of 1000 within each content type, and call the API separately for each of these chunks.
    //
    // The backend returns a complete list of Destination Projects for each chunk of source items
    // (let's call each copy a "slide"). Once we have all the slides, we combine them to create a
    // "projection" of all their statuses to show the user (the user must be able to move all the
    // items to a particular destination in order for it to be valid).
    let slidePromises = [];

    function pluckIds(items: Set<IIdItemInfo>) {
      return items.map(Set.PluckId);
    }

    // flow drafts are not permissioned as a full content type, filter out flow draft ids, the backend will then
    // fall back to user permissions for those ones
    function pluckFlowIds(items: Set<IIdItemInfo>) {
      return items.map(Set.PluckId).filter((id) => {
        return !hasDraftIdPrefix(id);
      });
    }

    if (projects.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'project', pluckIds(projects)));
    }
    if (workbooks.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'workbook', pluckIds(workbooks)));
    }
    if (datasources.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'datasource', pluckIds(datasources)));
    }
    if (getSessionManagementServiceInstance().flowsOnSiteEnabled() && flows.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'flow', pluckFlowIds(flows)));
    }
    if (dataRoles.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'dataRole', pluckIds(dataRoles)));
    }
    if (FeatureFlags.nlplensContentTypeEnabled && lenses.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'lens', pluckIds(lenses)));
    }
    if (metrics.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'metric', pluckIds(metrics)));
    }
    if (publishedConnections.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'publishedConnection', pluckIds(publishedConnections)));
    }

    return Promise.all(slidePromises).then((slides: RoseTree<NestedItemBasicInfo>[][]) => {

      // If all the slides are null, it means the user has no permission to move any of the content
      if (slides.every(x => x === null)) {
        return NoPermission;
      }
      let showPersonalSpaceOption =  isPersonalSpaceAvailable();
      personalSpaceStatus = personalSpaceStatus && isPersonalSpaceSupported(workbooks);
      return {
        topLevelDestinationStatus: getTopLevelDestinationStatus(datasources, flows, workbooks, dataRoles, metrics, publishedConnections),
        projects: makeDestinationItemSlideProjection(slides, showPersonalSpaceOption, personalSpaceStatus)
      };
    });
  }

  // this can be used later when we start to support more content type
  export function isPersonalSpaceSupported(workbook: Set<IContentInfo>): boolean {
    return workbook.size() > 0;
  }

  export function isPersonalSpaceAvailable(): boolean {
    if (!FeatureFlags.personalSpaceEnabled) {
      return false;
    }

    const personalSpaceInfo = selectPersonalSpace(getAppStore().getState());
    if (personalSpaceInfo.readOnly) {
      return false;
    }

    return true;
  }

  function addPersonalSpaceToProjection(projection: RoseTree<NestedItemBasicInfo>[], personalSpaceStatus: boolean) {
    const personalSpaceInfo = selectPersonalSpace(getAppStore().getState());
    let personalSpace: DestinationItem = {
      id: personalSpaceInfo.luid,
      name: '', // This is not used for PS but is a required field so have to be set
      itemType: DestinationItemType.personalSpace,
      childItemCount: 0,
      topLevelItem: true,
      status: personalSpaceStatus ? 'valid' : 'read_only',
    };
    return projection.concat(NestedItemsTreeHelper.convertItemListToTrees([personalSpace]));
  }

  //export for unit testing
  export function makeDestinationItemSlideProjection(slides: RoseTree<NestedItemBasicInfo>[][], showPersonalSpace: boolean, personalSpaceStatus: boolean) {
    let projection: RoseTree<NestedItemBasicInfo>[] = null;
    slides.forEach((slide) => {
      if (slide === null) {
        return;
      } else if (projection === null) {
        projection = slide;
      } else {
        projection = addSlideToProjection(slide, projection);
      }
    });
    if (FeatureFlags.personalSpaceEnabled && showPersonalSpace) {
      projection = addPersonalSpaceToProjection(projection, personalSpaceStatus);
    }

    return projection;
  }

  function addSlideToProjection(slide: RoseTree<NestedItemBasicInfo>[],
    projection: RoseTree<NestedItemBasicInfo>[]) {
    slide.forEach((node, i) => {
      if (node.node.hasChildren) {
        addSlideToProjection(node.children, projection[i].children);
      }

      // TODO: Should we have a precedence of the statuses here? Perhaps have a new status
      // that combines the messaging for the various statuses if they're different?
      if (node.node.status !== 'valid') {
        projection[i].node.status = node.node.status;
      }
    });
    return projection;
  }

  // export for unit testing
  export function getTopLevelDestinationStatus(
    datasources: Set<IContentInfo>,
    flows: Set<IContentInfo>,
    workbooks: Set<IContentInfo>,
    dataRoles: Set<IContentInfo>,
    metrics: Set<IContentInfo>,
    publishedConnections: Set<IContentInfo>
  ) {
    if (workbooks.size() > 0 || datasources.size() > 0 || flows.size() > 0 || dataRoles.size() > 0 || metrics.size() > 0 || publishedConnections.size() > 0) {
      return 'structurally_invalid';
    } else if (!getSessionManagementServiceInstance().isSessionSiteOrServerAdmin()) {
      return 'insufficient_permissions';
    } else {
      return 'valid';
    }
  }

  function getDestinationProjectTreeSlidePromisesForSourceItems(
    query: string,
    contentType: DestinationProjectContentType,
    itemIds: string[],
  ): Promise<RoseTree<NestedItemBasicInfo>[]>[] {

    // We can send only 1000 IDs to the API at a time, so make chunks of 1000 IDs and do the queries in parallel.
    const idBlocks = RequestHelper.makeBlocks(itemIds, RequestHelper.DefaultBlockSize);
    return idBlocks.map(idBlock => {
      const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(
        contentType, idBlock, query);

      return SliceableHelper.fetchAll(projectsQuery).then(createDestinationProjectTreeSlide);
    });
  }

  function createDestinationProjectTreeSlide(projects: ListResult<IDestinationProject>): RoseTree<NestedItemBasicInfo>[] {
    // Use the result of getDestinationProjects to infer if the user can move the items.
    // If no items are returned, the user doesn't have permission to move some of the selected items.
    // The check needs to be items[0] being undefined as the response that comes back is actually an array with first item as undefined.
    if (projects.items[0] === undefined) {
      return null;
    }

    const destinationProjects = DestinationProjectMapper.map(projects.items);
    return NestedItemsTreeHelper.convertItemListToTrees(destinationProjects);
  }

  //export for unit testing
  export function moveContent(
    projects: Set<IContentInfo>,
    workbooks: Set<IContentInfo>,
    datasources: Set<IContentInfo>,
    flows: Set<IContentInfo>,
    dataRoles: Set<IContentInfo>,
    lenses: Set<IContentInfo>,
    metrics: Set<IContentInfo>,
    publishedConnections: Set<IContentInfo>,
    content: IContentInfo[],
    selectedItem: NestedItemBasicInfo
  ) {
    const destinationProjectId = getDestinationProjectIdFromDialog(selectedItem);
    let moveFunctions: any[] = [];
    if(selectedItem.itemType === DestinationItemType.personalSpace) {
        moveFunctions.push(moveContentToContainer(
        selectedItem.id,
        selectedItem.itemType,
        getContentItemsList(content),
        content,
        selectedItem.name.length === 0 ? undefined: selectedItem.name));
    } else {
      moveFunctions = [
        moveProjects(projects, selectedItem, destinationProjectId),
        moveDataRoles(dataRoles, selectedItem, destinationProjectId),
        moveMetrics(metrics, selectedItem, destinationProjectId),
        movePublishedConnections(publishedConnections, selectedItem, destinationProjectId),
      ];
      if(!FeatureFlags.personalSpaceEnabled) {
        moveFunctions.push(
          moveWorkbooks(workbooks, selectedItem, destinationProjectId),
          moveDatasources(datasources, selectedItem, destinationProjectId));
      } else {
          let workbookAndDataSources: IContentInfo[] = content.filter(c => {
            return (c.contentType === WorkbookContentType.name || c.contentType === DatasourceContentType.name);
          });
          if (workbookAndDataSources.length > 0) {
            moveFunctions.push(moveContentToContainer(
              selectedItem.id,
              selectedItem.itemType,
              getContentItemsList(workbookAndDataSources),
              workbookAndDataSources,
              selectedItem.name.length === 0 ? undefined: selectedItem.name));
          }
      }
      if (getSessionManagementServiceInstance().flowsOnSiteEnabled()) {
        const actualFlows = flows.filter(flow => {
          return flow.draftStatus !== ContentDraftStatus.unpublished;
        });
        moveFunctions.push(moveFlows(actualFlows, selectedItem, destinationProjectId));
        const flowDrafts = flows.filter(flow => {
          return flow.draftStatus === ContentDraftStatus.unpublished;
        });
        moveFunctions.push(moveFlowDrafts(flowDrafts, selectedItem, destinationProjectId));
      }
      if (FeatureFlags.nlplensContentTypeEnabled) {
        moveFunctions.push(moveLenses(lenses, selectedItem, destinationProjectId));
      }
    }
    return Promise.all(moveFunctions);
  }

  function getContentItemsList(content: IContentInfo[]): ContentItem[] {
    let contentItems: ContentItem[] = [];
    if (content.length > 0) {
      contentItems.push(...content.map(content => ({
        contentId: content.id,
        contentType: content.contentType,
      })));
    }
    return contentItems;
  }

  const allContentTypes = [
    ProjectContentType,
    WorkbookContentType,
    DatasourceContentType,
    FlowContentType,
    DataRoleContentType,
    LensContentType,
    MetricContentType,
    PublishedConnectionContentType,
  ];

  function moveContentToContainer(containerId: string, containerType: string, contentItems: ContentItem[], contentItemInfos: IContentInfo[], projectName?: string): Promise<IMultipleContentTypeResult> {
    return Projects.moveToContainer(containerId, containerType, contentItems)
      .then((result: IMultipleContentTypeResult) => {
        const contentTypeToErrors = _.groupBy(result.errors, error => error.contentType);
        allContentTypes.forEach((contentType: IContentType) => {
          const contentTypeResult: IResult = {errors: contentTypeToErrors[contentType.name]};
          const contentTypeItems = Set.fromIdItemArray(contentItemInfos.filter(i => i.contentType === contentType.name));
          ContentActionNotification.displayItemizedToastsForResult(
            notificationForMoveContentToContainer(
            new MultipleItemResult(contentTypeItems, contentTypeResult),
            contentType.name as ExplorableItemType,
            containerType as DestinationItemType,
            projectName,
            containerId
          ));
        });
        return result;
      });
  }


  function moveProjects(projects: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (projects.size() < 1) {
      return Promise.resolve(null);
    }
    return Projects.moveToProject(destinationProjectId, projects.map(project => project.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(projects, result),
            Messages.moveProjectsToProjectSuccess,
            Messages.moveProjectsToProjectError,
            ProjectContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function moveMetrics(metrics: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (metrics.size() < 1) {
      return Promise.resolve(null);
    }
    return Metrics.moveToProject(destinationProjectId, metrics.map(metric => metric.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(metrics, result),
            Messages.moveMetricsToProjectSuccess,
            Messages.moveMetricsToProjectError,
            MetricContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function movePublishedConnections(publishedConnections: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (publishedConnections.size() < 1) {
      return Promise.resolve(null);
    }
    return PublishedConnections.moveToProject(destinationProjectId, publishedConnections.map(publishedConnection => publishedConnection.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(publishedConnections, result),
            Messages.movePublishedConnectionsToProjectSuccess,
            Messages.movePublishedConnectionsToProjectError,
            PublishedConnectionContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function moveWorkbooks(workbooks: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (workbooks.size() < 1) {
      return Promise.resolve(null);
    }
    return Workbooks.moveToProject(destinationProjectId, workbooks.map(workbook => workbook.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(workbooks, result),
            Messages.moveWorkbooksToProjectSuccess,
            Messages.moveWorkbooksToProjectError,
            WorkbookContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function moveFlowDrafts(flowDrafts: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (flowDrafts.size() < 1) {
      return Promise.resolve(null);
    }
    return Flows.moveDraftsToProject(destinationProjectId, flowDrafts.map(flow => flow.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(flowDrafts, result),
            Messages.moveFlowDraftsToProjectSuccess,
            Messages.moveFlowDraftsToProjectError,
            FlowContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function moveFlows(flows: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (flows.size() < 1) {
      return Promise.resolve(null);
    }
    return Flows.moveToProject(destinationProjectId, flows.map(flow => flow.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(flows, result),
            Messages.moveFlowsToProjectSuccess,
            Messages.moveFlowsToProjectError,
            FlowContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function moveDataRoles(dataRoles: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (dataRoles.size() < 1) {
      return Promise.resolve(null);
    }
    return DataRoles.moveToProject(destinationProjectId, dataRoles.map(dr => dr.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(dataRoles, result),
            Messages.moveDataRolesToProjectSuccess,
            Messages.moveDataRolesToProjectError,
            DataRoleContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function moveLenses(lenses: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (lenses.size() < 1) {
      return Promise.resolve(null);
    }
    return Lenses.moveToProject(destinationProjectId, lenses.map(dr => dr.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(lenses, result),
            Messages.moveLensesToProjectSuccess,
            Messages.moveLensesToProjectError,
            LensContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function moveDatasources(datasources: Set<IContentInfo>, selectedItem: NestedItemBasicInfo, destinationProjectId: string): Promise<IResult> {
    if (datasources.size() < 1) {
      return Promise.resolve(null);
    }

    return DataSources.moveToProject(destinationProjectId, datasources.map(datasource => datasource.id))
      .then((result: IResult) => {
        ContentActionNotification.displayItemizedToastsForResult(
          notification(
            new MultipleItemResult(datasources, result),
            Messages.moveDatasourcesToProjectSuccess,
            Messages.moveDatasourcesToProjectError,
            DatasourceContentType,
            selectedItem
          )
        );
        return result;
      });
  }

  function notification(
    result: MultipleItemResult<INamedItemInfo>,
    successId: (a: {
      ITEM_COUNT: number;
      ITEM_COUNT_LOC: string;
      PROJECT_NAME: string;
    }) => string,
    errorId: (a: {
      ERROR_COUNT: number;
      ERROR_COUNT_LOC: string;
      PROJECT_NAME: string;
    }) => string,
    itemType: IContentType,
    project: IProjectName): MultipleItemResultNotificationValues {

    return createMultipleItemResultNotificationValues(result,
      {
        describeSuccessSummary: (successCount: number) =>
          successId({
            ITEM_COUNT: successCount,
            PROJECT_NAME: L10n.elideIfLong(project.name),
            ITEM_COUNT_LOC: Formatters.formatCount(successCount)
          }),

        describeErrorSummary: (errorCount: number) =>
          errorId({
            ERROR_COUNT: errorCount,
            PROJECT_NAME: L10n.elideIfLong(project.name),
            ERROR_COUNT_LOC: Formatters.formatCount(errorCount)
          }),

        describeErrorDetail: (errorItem: IErrorItem<INamedItemInfo>) => {
          if (!errorItem.item || !errorItem.error) return;
          if (itemType === ProjectContentType) {
            return ProjectErrors.errorDetailsFor(errorItem as IErrorItem<IProjectBase>);
          } else if (itemType === DatasourceContentType) {
            return DataSourceErrors.errorDetailsFor(errorItem as IErrorItem<IDataSourceBase>);
          } else if (itemType === WorkbookContentType) {
            return WorkbookErrors.errorDetailsFor(errorItem as IErrorItem<IWorkbookBase>);
          } else if (itemType === FlowContentType) {
            return FlowErrors.errorDetailsFor(errorItem as IErrorItem<FlowBaseInfo>);
          } else if (itemType === DataRoleContentType) {
            return DataRoleErrors.errorDetailsFor(errorItem as IErrorItem<IDataRoleBase>);
          } else if (itemType === LensContentType) {
            return LensErrors.errorDetailsFor(errorItem as IErrorItem<ILensBase>);
          } else if (itemType === MetricContentType) {
            return MetricErrors.errorDetailsFor(errorItem as IErrorItem<IMetricInfo>);
          } else if (itemType === PublishedConnectionContentType) {
            return PublishedConnectionErrors.errorDetailsFor(errorItem as IErrorItem<IPublishedConnectionInfo>);
          }
        }
      }
    );
  }

  function notificationForMoveContentToContainer(
    result: MultipleItemResult<INamedItemInfo>,
    itemTypeName: ExplorableItemType,
    destinationType: DestinationItemType,
    projectName?: string,
    projectId?: string): MultipleItemResultNotificationValues {

    return createMultipleItemResultNotificationValues(result,
      {
        describeSuccessSummary: (successCount: number) =>
          getSuccessMessageForMoveContentToContainer(destinationType, itemTypeName, successCount, projectName),

        successActionMessage: (successCount: number) => {
          switch (destinationType) {
            case DestinationItemType.personalSpace:
              return Messages.goToPersonalSpace();
            case DestinationItemType.project:
              return Messages.goToProject();
          }
        },

        describeErrorSummary: (errorCount: number) =>
          getErrorMessageForMoveContentToContainer(destinationType, itemTypeName, errorCount, projectName),

        describeErrorDetail: (errorItem: IErrorItem<INamedItemInfo>) => {
          if (!errorItem.item || !errorItem.error) return;
          if (itemTypeName === DatasourceContentType.name) {
            return DataSourceErrors.moveContentToContainerErrorMessageDetailsFor(errorItem as IErrorItem<IDataSourceBase>, destinationType);
          } else if (itemTypeName === WorkbookContentType.name) {
            return WorkbookErrors.moveContentToContainerErrorMessageDetailsFor(errorItem as IErrorItem<IWorkbookBase>, destinationType);
          } else {
            return moveContentToContainerErrorMessageDetail(errorItem, itemTypeName);
          }
        }
      },
      successAction(destinationType, projectId),
    );
  }

  function moveContentToContainerErrorMessageDetail(errorItem: IErrorItem<INamedItemInfo>, itemTypeName: ExplorableItemType): IErrorDetail {
    return {
      itemName: errorItem.item.name,
      itemUrl: null,
      errorMessage: moveContentToContainerErrorMessageForContent(errorItem.error, itemTypeName)
    };
  }

  function moveContentToContainerErrorMessageForContent(error: IError, itemTypeName: ExplorableItemType): string {
    switch(error.code) {
      case ContentErrorCode.INVALID_CONTENT_TYPE:
      case CommonErrorCode.UnexpectedParameterValue: // projects
        return Messages.moveContentToPersonalSpaceErrorDetail({CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemTypeName, true))});
      default:
        return Messages.errorUnknown();
    }
  }

  function successAction(containerType: DestinationItemType, projectId?: string) {
    return () => {
      const siteUrls = siteUrlsSelector(getAppStore().getState());
      switch(containerType) {
        case DestinationItemType.personalSpace:
          Location.instance.setUrl(siteUrls.personalSpaceUrl());
          break;
        case DestinationItemType.project:
          Location.instance.setUrl(siteUrls.projectUrl(projectId));
          break;
        default:
          break;
      }
    };
  }

  function getSuccessMessageForMoveContentToContainer(containerType: DestinationItemType, itemType: ExplorableItemType, count: number, projectName?: string) {
    const toPersonalSpace = containerType === DestinationItemType.personalSpace;
    switch(itemType) {
      case 'workbook':
        return toPersonalSpace ?
          Messages.moveWorkbooksToPersonalSpaceSuccess({ITEM_COUNT: count, ITEM_COUNT_LOC: Formatters.formatCount(count)}):
          Messages.moveWorkbooksToProjectSuccess({ITEM_COUNT: count, ITEM_COUNT_LOC: Formatters.formatCount(count), PROJECT_NAME: projectName});
      case 'datasource':
        return toPersonalSpace ?
        Messages.moveDatasourcesToPersonalSpaceSuccess({ITEM_COUNT: count, ITEM_COUNT_LOC: Formatters.formatCount(count)}):
        Messages.moveDatasourcesToProjectSuccess({ITEM_COUNT: count, ITEM_COUNT_LOC: Formatters.formatCount(count), PROJECT_NAME: projectName});
      default:
        return Messages.unexpectedError();
    }
  }

  function getErrorMessageForMoveContentToContainer(containerType: DestinationItemType, itemTypeName: ExplorableItemType, count: number, projectName?: string) {
    if (containerType === DestinationItemType.personalSpace) {
      return Messages.moveContentToPersonalSpaceError({
        ERROR_COUNT: L10n.localizedCount(count),
        CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemTypeName, count > 1))
      });
    } else {
      return Messages.moveContentToProjectError({
        ERROR_COUNT: L10n.localizedCount(count),
        CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemTypeName, count > 1)),
        PROJECT_NAME: projectName
      });
    }
  }

  function getLocalizedContentType(contentType: ExplorableItemType, isPlural: boolean): string {
    const typeDefinition = getExplorableItemDefinition(contentType);
    if (isUndefined(typeDefinition)) {
      throw new Error('Tried to localize unrecognized content type string: ' + contentType);
    }
    return isPlural ? typeDefinition.pluralName() : typeDefinition.singularName();
  }
}

export type MoveContentAction = typeof MoveContentAction;
